import {
  of,
  map,
  tap,
  from,
  timer,
  toArray,
  forkJoin,
  mergeMap,
  switchMap,
  delayWhen,
  concatMap,
  catchError,
  Observable,
  firstValueFrom,
} from "rxjs";
import { inject, Injector, Injectable } from "@angular/core";
import { HttpService } from "../http-service/http-client.service";
import { StorageService } from "../storage-service/storage.service";
import { OfflineAssessmentCompletionService } from "../offline-assessment-completion/offline-assessment-completion.service";
import {
  AllCacheModel,
  GuidanceOfflineModel,
  OfflineAssessmentUploadModel,
  CountryWiseSiteAssessmentsModel,
} from "src/app/store/models/offline-configuration.model";
import {
  ActionModel,
  FindingModel,
  AssessmentModel,
  FindingResponseModel,
  AssessmentAssetModel,
  AssessmentResponseModel,
  AllFindingsAndActionsModel,
  SiteAssessmentFindingsActionsModel,
} from "src/app/store/models/assesment.model";
import {
  DefectLevel,
  DefectType,
  GuidanceItem,
} from "src/app/store/models/guidance.model";
import { BlobResponseModel } from "src/app/store/models/user.model";
import { MM_InventoryModel } from "src/app/store/models/materialManagement.model";
import { MM_ControlInventoryModel } from "src/app/store/models/mmControlManagement.model";
import { FirebaseService } from "../firebase-service/firebase.service";

@Injectable({
  providedIn: "root",
})
export class OfflineManagerService {
  private storageService = inject(StorageService);
  private injector = inject(Injector);
  private offlineAssessmentCompletionService = inject(
    OfflineAssessmentCompletionService,
  );
  httpService = inject(HttpService);

  constructor() {}

  async replaceSiteInAllCache(siteId: string): Promise<void> {
    if (!siteId) {
      return;
    }

    const firebaseService = this.injector.get(FirebaseService);

    const [freshSite, freshInventories] = await Promise.all([
      firstValueFrom(firebaseService.getSiteByDocId(siteId)).catch((err) => {
        return null;
      }),
      firstValueFrom(
        firebaseService.getActiveMMInventoryBySite({ siteId }),
      ).catch((err) => {
        return null;
      }),
    ]);

    const allCache = (await this.storageService.get("allCache")) as
      | AllCacheModel[]
      | null;
    if (!allCache?.length) {
      return;
    }

    const sites = allCache[0]?.sites;
    if (freshSite?.id && sites?.length) {
      const siteIndex = sites.findIndex((site) => site?.id === siteId);
      if (siteIndex !== -1) {
        sites[siteIndex] = freshSite;
      }
    } else {
    }

    if (freshInventories) {
      const inventoryEntry = allCache.find(
        (item: AllCacheModel & { mmInventory?: MM_InventoryModel[] }) =>
          item?.mmInventory,
      ) as { mmInventory: MM_InventoryModel[] } | undefined;

      if (inventoryEntry) {
        inventoryEntry.mmInventory = [
          ...inventoryEntry.mmInventory.filter(
            (inventory) => inventory?.siteId !== siteId,
          ),
          ...freshInventories,
        ];
      }
    } else {
    }

    await this.storageService.set("allCache", allCache);

  }

  async refreshMMControlInventoryCache(): Promise<void> {
    const firebaseService = this.injector.get(FirebaseService);

    try {
      const controls = await firstValueFrom(
        firebaseService.getMMControlInventoryList(),
      );

      if (!controls?.length) {
        console.warn("[refreshMMControlInventoryCache] no controls fetched");
        return;
      }

      const converted = await firstValueFrom(
        this.convertMMControlInventoryImages(controls, firebaseService),
      );

      await this.storageService.set("mmControlInventoryOffline", converted);
    } catch (err) {
      console.error("[refreshMMControlInventoryCache] failed:", err);
    }
  }

  private convertMMControlInventoryImages(
    controls: MM_ControlInventoryModel[],
    firebaseService: FirebaseService,
  ): Observable<MM_ControlInventoryModel[]> {
    const CONCURRENCY = 10;

    return from(controls).pipe(
      mergeMap((control) => {
        if (!control?.riskLevels?.length) {
          return of(control);
        }

        return from(control.riskLevels).pipe(
          mergeMap((level) => {
            if (!level?.memberType?.length) {
              return of(level);
            }

            return from(level.memberType).pipe(
              mergeMap((member) => {
                const img = member?.img;

                if (
                  !img ||
                  !img.src ||
                  !img.id ||
                  img.src.startsWith("data:")
                ) {
                  return of(member);
                }

                return firebaseService
                  .GetImageBlob(img.id, img.fileType || "image/webp")
                  .pipe(
                    map((res) => ({
                      ...member,
                      img: {
                        ...img,
                        src: res || img.src || "",
                      },
                    })),
                    catchError(() => of(member)),
                  );
              }, CONCURRENCY),
              toArray(),
              map((convertedMembers) => ({
                ...level,
                memberType: convertedMembers,
              })),
              catchError(() => of(level)),
            );
          }, CONCURRENCY),
          toArray(),
          map((convertedLevels) => ({
            ...control,
            riskLevels: convertedLevels,
          })),
          catchError(() => of(control)),
        );
      }, CONCURRENCY),
      toArray(),
    );
  }

  getOfflineSiteData(
    locationId: string,
  ): Observable<SiteAssessmentFindingsActionsModel> {
    return from(
      Promise.all([
        this.storageService.get("allCache"),
        this.storageService.get("countryWiseSiteAssessments"),
      ]),
    ).pipe(
      switchMap(
        ([allCache, siteAssessments]: [
          AllCacheModel[],
          CountryWiseSiteAssessmentsModel,
        ]) => {
          const sites = allCache[0]?.sites ?? [];
          const site = sites.find((_site) => _site?.id === locationId);

          if (!site) {
            return of({} as SiteAssessmentFindingsActionsModel);
          }

          const siteData = siteAssessments?.[locationId] ?? [];

          const assessments =
            siteData.map((data) => data.assessment).flat() ?? [];

          const findings = siteData
            .map((data) => data.findings)
            .flat()
            .filter((finding): finding is FindingModel => !!finding);

          const actions = siteData
            .map((data) => data.actions)
            .flat()
            .filter((action): action is ActionModel => !!action);

          return from(
            Promise.all(
              assessments.map(async (assessment) => ({
                ...assessment,
                assets:
                  await this.offlineAssessmentCompletionService.mergeOverrides(
                    assessment?.id,
                    assessment?.assets ?? [],
                  ),
              })),
            ),
          ).pipe(
            map(
              (mergedAssessments) =>
                ({
                  site,
                  assessments: mergedAssessments,
                  findings,
                  actions,
                }) as SiteAssessmentFindingsActionsModel,
            ),
          );
        },
      ),
    );
  }

  getOfflineFindingsActionsForAssessment(
    siteId: string,
    assessmentId: string,
  ): Observable<{ findings: FindingModel[]; actions: ActionModel[] }> {
    return from(this.storageService.get("countryWiseSiteAssessments")).pipe(
      map((sites: CountryWiseSiteAssessmentsModel) => {
        const siteData = sites?.[siteId] ?? [];
        if (!siteData) {
          return { findings: [], actions: [] };
        }
        const assessmentData = siteData.find(
          (data) => data?.assessment?.id === assessmentId,
        );

        if (!assessmentData) {
          return { findings: [], actions: [] };
        }

        return {
          findings: assessmentData.findings || [],
          actions: assessmentData.actions || [],
        };
      }),
    );
  }
  getAssessmentByIdOffline(
    assessmentId: string,
  ): Observable<AssessmentModel | null> {
    return from(this.storageService.get("countryWiseSiteAssessments")).pipe(
      switchMap((sites: CountryWiseSiteAssessmentsModel) => {
        const siteIds = Object.keys(sites ?? {});

        for (const siteId of siteIds) {
          const siteData = sites[siteId] ?? [];
          const match = siteData.find(
            (data) => data?.assessment?.id === assessmentId,
          );

          if (match?.assessment) {
            return from(
              this.offlineAssessmentCompletionService.mergeOverrides(
                assessmentId,
                match.assessment.assets ?? [],
              ),
            ).pipe(
              map(
                (assets) =>
                  ({ ...match.assessment, assets }) as AssessmentModel,
              ),
            );
          }
        }

        return of(null);
      }),
    );
  }

  getOfflineDefectTypeByName(defectTypeName: string): Observable<DefectType[]> {
    return from(this.storageService.get("guidanceInformation")).pipe(
      map((guidancePart: GuidanceOfflineModel) => {
        const defectTypes = guidancePart?.defectType ?? [];

        return defectTypes.filter(
          (defect) => defect?.defectTypeName === defectTypeName,
        );
      }),
    );
  }

  getAssetImageFromAssessmentOffline(
    assessmentId: string,
    assetId: string,
    siteId: string,
  ): Observable<string | undefined> {
    return from(this.storageService.get("countryWiseSiteAssessments")).pipe(
      map((sites: CountryWiseSiteAssessmentsModel) => {
        const siteData = sites?.[siteId] ?? [];
        const assessmentBlock = siteData.find(
          (data) => data?.assessment?.id === assessmentId,
        );

        if (!assessmentBlock) return undefined;

        const assets = assessmentBlock?.assessment?.assets ?? [];

        const asset = assets.find((assessment) => assessment?.id === assetId);

        return asset?.assetImage;
      }),
    );
  }

  getAllActionsFindingsByAssesmentIdWithoutUserFilterOffline(
    assessmentId: string,
    siteId: string,
  ): Observable<AllFindingsAndActionsModel> {
    return from(this.storageService.get("countryWiseSiteAssessments")).pipe(
      map((sites: CountryWiseSiteAssessmentsModel) => {
        const siteData = sites?.[siteId] ?? [];
        if (!siteData) {
          return { findings: [], actions: [] };
        }

        const assessmentBlock = siteData.find(
          (data) => data?.assessment?.id === assessmentId,
        );
        if (!assessmentBlock) {
          return { findings: [], actions: [] };
        }

        const { assessment, findings = [], actions = [] } = assessmentBlock;

        const assetIds = assessment!.assets?.map((_asset) => _asset?.id) ?? [];

        const filteredFindings = findings.filter(
          (_finding) =>
            assetIds.includes(_finding?.assetId) &&
            _finding?.isActive !== false,
        );

        const findingsWithActions = filteredFindings.map((finding) => ({
          ...finding,
          actions: actions.filter(
            (action) => action?.findingId === finding?.id,
          ),
        }));

        const actionsWithFindings = actions.map((action) => ({
          ...action,
          finding:
            filteredFindings.find(
              (finding) => finding?.id === action?.findingId,
            ) ?? null,
        }));

        return {
          findings: findingsWithActions,
          actions: actionsWithFindings,
        };
      }),
    );
  }

  getActiveMMInventoryBySiteOffline(data: {
    siteId: string;
  }): Observable<MM_InventoryModel[] | null> {
    return from(this.storageService.get("allCache")).pipe(
      map((allCache) => {
        const mmInventory: MM_InventoryModel[] =
          allCache?.find((item: any) => item?.mmInventory)?.mmInventory || [];

        return mmInventory.filter(
          (inventory) =>
            inventory?.siteId === data.siteId &&
            inventory?.status === "Published" &&
            inventory?.identification?.statusSection?.status !== "SIMULATION",
        );
      }),
      catchError((error) => {
        console.error("Error loading MM Inventory from cache", error);
        return of(null);
      }),
    );
  }

  getMMControlInventoryWithAssetsOffline(
    assetName: string,
  ): Observable<MM_ControlInventoryModel[]> {
    return from(this.storageService.get("mmControlInventoryOffline")).pipe(
      map((mmControlInventory: MM_ControlInventoryModel[]) =>
        (mmControlInventory || [])
          .filter(
            (control) =>
              Array.isArray(control.asset) && control.asset.includes(assetName),
          )
          .sort((a, b) => {
            const aDate = a.createdDate ? new Date(a.createdDate).getTime() : 0;

            const bDate = b.createdDate ? new Date(b.createdDate).getTime() : 0;

            return bDate - aDate;
          }),
      ),
      catchError((error) => {
        console.error("Error loading MM Control Inventory from cache", error);

        return of([]);
      }),
    );
  }
  /*
  convertAssessmentAssetImages(
    assessments: OfflineAssessmentUploadModel[],
    onProgress?: () => void,
  ): Observable<OfflineAssessmentUploadModel[]> {
    if (!assessments?.length) {
      return of(assessments);
    }

    return from(assessments).pipe(
      concatMap((assessmentBlock): Observable<OfflineAssessmentUploadModel> => {
        const assets = assessmentBlock?.assessment?.assets ?? [];

        if (!assets.length) {
          return of(assessmentBlock);
        }

        return from(assets).pipe(
          concatMap((asset: AssessmentAssetModel) => {
            const img = asset?.assetImage;

            if (!img || (typeof img === "string" && img.startsWith("data:"))) {
              onProgress?.();
              return of(asset);
            }

            return of(null).pipe(
              delayWhen(() => timer(100)),
              concatMap(() => this.httpService.GetUrlToBlob({ url: img })),
              map((res: BlobResponseModel) => ({
                ...asset,
                assetImage: res?.data?.dataUrl ?? img,
              })),
              catchError(() => of(asset)),
              tap(() => onProgress?.()),
            );
          }),
          toArray(),
          map(
            (convertedAssets): OfflineAssessmentUploadModel => ({
              ...assessmentBlock,
              assessment: {
                ...assessmentBlock.assessment,
                assets: convertedAssets as AssessmentAssetModel[],
              },
            }),
          ),
        );
      }),
      toArray(),
    );
  }

  convertGuidanceSubtypeImages(
    guidanceSubtype: GuidanceItem[],
  ): Observable<GuidanceItem[]> {
    return from(guidanceSubtype).pipe(
      mergeMap((item): Observable<GuidanceItem> => {
        const img = item.assetImage;

        if (!img || !img.src || img.src.startsWith("data:")) {
          return of(item);
        }

        return this.httpService.GetUrlToBlob({ url: img.src }).pipe(
          map((res: BlobResponseModel) => ({
            ...item,
            assetImage: {
              ...img,
              src: res.data?.dataUrl || img.src || "",
            },
          })),
          catchError(() => of(item)),
        );
      }, 3),
      toArray(),
    );
  }
  convertDefectTypeImages(defectTypes: DefectType[]): Observable<DefectType[]> {
    return from(defectTypes).pipe(
      mergeMap(
        (defectType) =>
          this.convertDefectLevels(defectType).pipe(
            catchError(() => of(defectType)),
          ),
        3,
      ),
      toArray(),
    );
  }
  private convertDefectLevels(defectType: DefectType): Observable<DefectType> {
    if (!defectType?.defectLevels?.length) {
      return of(defectType);
    }

    return from(defectType.defectLevels).pipe(
      mergeMap(
        (level) =>
          this.convertMemberTypeImages(level).pipe(catchError(() => of(level))),
        3,
      ),
      toArray(),
      map((convertedLevels) => ({
        ...defectType,
        defectLevels: convertedLevels,
      })),
    );
  }
  private convertMemberTypeImages(level: DefectLevel): Observable<DefectLevel> {
    if (!level?.memberType?.length) {
      return of(level);
    }

    return from(level.memberType).pipe(
      mergeMap((member) => {
        const img = member?.img;

        if (!img || !img.src || img.src.startsWith("data:")) {
          return of(member);
        }

        return this.httpService.GetUrlToBlob({ url: img.src }).pipe(
          map((res: BlobResponseModel) => ({
            ...member,
            img: {
              ...img,
              src: res?.data?.dataUrl || img.src || "",
            },
          })),
          catchError(() => of(member)),
        );
      }, 3),
      toArray(),
      map((convertedMembers) => ({
        ...level,
        memberType: convertedMembers,
      })),
    );
  }
  */
}
